import { v4 as uuidv4 } from 'uuid';
import { Product as ProductDTO } from '@setvi/shared/interfaces';
import {
  QuoteDTO,
  QuoteProductDTO,
  QuoteItemDTO,
  QuoteTotals,
  QuoteSectionDTO,
  UpdateQuoteBody,
  QuoteItemAPIDTO
} from '@setvi/shared/services/react-query';
import { calculateGrossProfitPercentage } from './pricing-helpers';

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * Pricing data structure from API
 */
export interface PricingData {
  sku: string;
  sell: number;
  cost: number;
  isEditable: boolean;
  minimumGP?: number;
  maximumGP?: number;
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get the ID of a quote item (handles both id and _id fields)
 * Centralizes the common pattern of `item.id || item._id`
 */
export const getItemId = (item: QuoteItemDTO): string => item.id || item._id;

/**
 * Generic mapper for quote items that handles recursive traversal
 * Use this to apply transformations to all products in a quote structure
 *
 * @param items - Array of quote items to process
 * @param productMapper - Function to transform product items
 * @returns Transformed items array
 */
export const mapQuoteItems = (
  items: QuoteItemDTO[],
  productMapper: (product: QuoteProductDTO) => QuoteProductDTO
): QuoteItemDTO[] =>
  items.map(item => {
    if (item.type === 'product') {
      const product = item as QuoteProductDTO;
      const mappedProduct = productMapper(product);

      // Recursively process children
      if (mappedProduct.children && mappedProduct.children.length > 0) {
        return {
          ...mappedProduct,
          children: mapQuoteItems(
            mappedProduct.children,
            productMapper
          ) as QuoteProductDTO[]
        };
      }

      return mappedProduct;
    }

    if (item.type === 'section' && 'children' in item && item.children) {
      return {
        ...item,
        children: mapQuoteItems(
          item.children,
          productMapper
        ) as QuoteProductDTO[]
      };
    }

    return item;
  });

// ============================================================================
// Product Initialization
// ============================================================================

/**
 * Initialize a product with default quote-specific fields
 * Maps catalog product pricing to quote fields:
 * - product.price (catalog sell price) -> sell
 * - product.customerCost (customer-specific cost) or product.cost -> cost
 * Calculates initial GP values from sell and cost
 * Sets isPricingFetching flag for products that need pricing from API
 */
export const initializeProduct = (
  product: ProductDTO,
  parentId: string | null,
  type?: 'section' | 'product'
): QuoteProductDTO => {
  // Map catalog price fields to quote fields
  const sell = product.price || product.sell || 0;
  const cost = product.customerCost || product.cost || 0;

  // Calculate GP per unit (matching old quotes system)
  const totalGrossProfit = sell - cost;
  const totalGrossProfitPercentage = calculateGrossProfitPercentage(cost, sell);

  // Determine if we need to fetch pricing (only for non-freehand products)
  const needsPricingFetch = !(product as any).isFreeHand;

  return {
    ...product,
    id: uuidv4(),
    _id: product._id,
    type: 'product',
    quantity:
      product?.overrideMinimumQuoteQuantity === false
        ? product?.minimumQuoteQuantity || 1
        : product?.quantity || 1,
    parentID: parentId,
    productId: product._id,
    children: [],
    note: null,
    isPricingFetching: needsPricingFetch,
    totalGrossProfit,
    totalGrossProfitPercentage,
    sell,
    cost,
    isEditable: product.isEditable,
    images: product.images,
    imageUrl: product.imageUrl,
    sku: product.sku,
    name: product.name,
    vendor: product.vendor,
    rfqItemId: (product as any).rfqItemId || undefined,
    ...((product as any).isFreeHand && { isFreeHand: true }),
    // Set default label fields for children (options/accessories)
    ...(parentId &&
      type !== 'section' && {
        labelType: 'option',
        customLabel: '',
        includeInTotalCost: false
      }),
    minimumQuoteQuantity: product?.minimumQuoteQuantity ?? 1,
    overrideMinimumQuoteQuantity: product?.overrideMinimumQuoteQuantity ?? true
  };
};

/**
 * Add products optimistically to quote
 */
export const addProductsOptimistically = (
  quote: QuoteDTO | undefined,
  products: ProductDTO[],
  parentId?: string | null
): QuoteDTO | undefined => {
  if (!quote) return quote;

  // Find parent type if parentId is provided
  let parentType: 'section' | 'product' | undefined;
  if (parentId) {
    const findParent = (items: QuoteItemDTO[]): QuoteItemDTO | null => {
      for (const item of items) {
        if (getItemId(item) === parentId) return item;
        if ('children' in item && item.children?.length) {
          const found = findParent(item.children);
          if (found) return found;
        }
      }
      return null;
    };
    const parentItem = findParent(quote.items);
    if (parentItem) {
      parentType = parentItem.type as 'section' | 'product';
    }
  }

  const initializedProducts = products.map(p =>
    initializeProduct(p, parentId || null, parentType)
  );

  // If no parentId, add to root level
  if (!parentId) {
    return {
      ...quote,
      items: [...quote.items, ...initializedProducts]
    };
  }

  // Add to parent (section or product with children)
  const addToParent = (items: QuoteItemDTO[]): QuoteItemDTO[] =>
    items.map(item => {
      const itemId = getItemId(item);

      if (itemId === parentId) {
        return {
          ...item,
          children: [...(item.children || []), ...initializedProducts]
        } as QuoteItemDTO;
      }

      if ('children' in item && item.children?.length) {
        return {
          ...item,
          children: addToParent(item.children)
        } as QuoteItemDTO;
      }

      return item;
    });

  return {
    ...quote,
    items: addToParent(quote.items)
  };
};

/**
 * Remove item optimistically from quote
 * If removing the last child of a parent product, clear parent's labelType and customLabel
 */
export const removeItemOptimistically = (
  quote: QuoteDTO | undefined,
  itemId: string
): QuoteDTO | undefined => {
  if (!quote) return quote;

  const removeFromList = (items: QuoteItemDTO[]): QuoteItemDTO[] =>
    items
      .filter(item => getItemId(item) !== itemId)
      .map(item => {
        if ('children' in item && item.children?.length) {
          const filteredChildren = removeFromList(item.children);

          // If this is a product and we're removing the last child, clear label fields
          if (item.type === 'product' && filteredChildren.length === 0) {
            return {
              ...item,
              children: filteredChildren,
              labelType: '',
              customLabel: ''
            } as QuoteItemDTO;
          }

          return {
            ...item,
            children: filteredChildren
          } as QuoteItemDTO;
        }
        return item;
      });

  return {
    ...quote,
    items: removeFromList(quote.items)
  };
};

/**
 * Remove section but keep its items - items are moved to root level
 * at the position where the section was
 */
export const removeSectionKeepItemsOptimistically = (
  quote: QuoteDTO | undefined,
  sectionId: string
): QuoteDTO | undefined => {
  if (!quote) return quote;

  const newItems: QuoteItemDTO[] = [];

  for (const item of quote.items) {
    if (getItemId(item) === sectionId && item.type === 'section') {
      // Found the section - add its children to root level instead of the section
      if ('children' in item && item.children?.length) {
        // Clear parentId from children since they're moving to root
        const childrenAtRoot = item.children.map(child => ({
          ...child,
          parentId: null as string | null
        }));
        newItems.push(...(childrenAtRoot as QuoteItemDTO[]));
      }
      // Don't add the section itself
    } else {
      // Keep other items as-is
      newItems.push(item);
    }
  }

  return {
    ...quote,
    items: newItems
  };
};

/**
 * Move an item to a different section optimistically
 * Removes the item from its current location and adds it to the target section
 */
export const moveItemToSectionOptimistically = (
  quote: QuoteDTO | undefined,
  itemId: string,
  targetSectionId: string | null
): QuoteDTO | undefined => {
  if (!quote) return quote;

  // First, find and extract the item to move
  let itemToMove: QuoteItemDTO | null = null;

  const removeItemFromList = (items: QuoteItemDTO[]): QuoteItemDTO[] =>
    items
      .filter(item => {
        if (getItemId(item) === itemId) {
          itemToMove = { ...item };
          return false; // Remove from current location
        }
        return true;
      })
      .map(item => {
        if ('children' in item && item.children?.length) {
          return {
            ...item,
            children: removeItemFromList(item.children)
          } as QuoteItemDTO;
        }
        return item;
      });

  // Remove item from its current location
  const itemsWithoutMovedItem = removeItemFromList(quote.items);

  if (!itemToMove) {
    return quote; // Item not found, return unchanged
  }

  // Update the item's parentID to the target section (or null for root)
  const movedItem = {
    ...itemToMove,
    parentID: targetSectionId
  } as QuoteItemDTO;

  // If moving to root (null), add to top-level items
  if (targetSectionId === null) {
    return {
      ...quote,
      items: [...itemsWithoutMovedItem, movedItem]
    };
  }

  // Add the item to the target section
  const addToSection = (items: QuoteItemDTO[]): QuoteItemDTO[] =>
    items.map(item => {
      if (getItemId(item) === targetSectionId && item.type === 'section') {
        return {
          ...item,
          children: [...(item.children || []), movedItem]
        } as QuoteItemDTO;
      }

      if ('children' in item && item.children?.length) {
        return {
          ...item,
          children: addToSection(item.children)
        } as QuoteItemDTO;
      }

      return item;
    });

  return {
    ...quote,
    items: addToSection(itemsWithoutMovedItem)
  };
};

/**
 * Move multiple items to a section optimistically in a single update
 * More efficient than calling moveItemToSectionOptimistically multiple times
 */
export const moveItemsToSectionOptimistically = (
  quote: QuoteDTO | undefined,
  itemIds: string[],
  targetSectionId: string | null
): QuoteDTO | undefined => {
  if (!quote || itemIds.length === 0) return quote;

  // Apply moves sequentially to get final state
  let result = quote;
  for (const itemId of itemIds) {
    result = moveItemToSectionOptimistically(result, itemId, targetSectionId);
  }
  return result;
};

/**
 * Update item optimistically in quote
 */
export const updateItemOptimistically = (
  quote: QuoteDTO | undefined,
  itemId: string,
  updates: {
    quantity?: number;
    sell?: number;
    cost?: number;
    note?: string;
    totalGrossProfit?: number;
    totalGrossProfitPercentage?: number;
    labelType?: string;
    customLabel?: string;
    includeInTotalCost?: boolean;
  }
): QuoteDTO | undefined => {
  if (!quote) return quote;

  const updateInList = (items: QuoteItemDTO[]): QuoteItemDTO[] =>
    items.map(item => {
      if (getItemId(item) === itemId && item.type === 'product') {
        const product = item as QuoteProductDTO;
        const updatedProduct = { ...product, ...updates };

        // Recalculate GP per unit if sell or cost changed (matching old quotes system)
        if (updates.sell !== undefined || updates.cost !== undefined) {
          const sell = updates.sell ?? product.sell;
          const cost = updates.cost ?? product.cost;

          updatedProduct.totalGrossProfit = sell - cost;
          updatedProduct.totalGrossProfitPercentage =
            calculateGrossProfitPercentage(cost, sell);
        }

        return updatedProduct;
      }

      if ('children' in item && item.children?.length) {
        return {
          ...item,
          children: updateInList(item.children)
        } as QuoteItemDTO;
      }

      return item;
    });

  return {
    ...quote,
    items: updateInList(quote.items)
  };
};

/**
 * Find item by ID in nested structure
 */
export const findItemById = (
  items: QuoteItemDTO[],
  itemId: string
): QuoteItemDTO | null => {
  for (const item of items) {
    if (getItemId(item) === itemId) {
      return item;
    }

    if ('children' in item && item.children?.length) {
      const found = findItemById(item.children, itemId);
      if (found) return found;
    }
  }

  return null;
};

/**
 * Find parent of item
 */
export const findParentOfItem = (
  items: QuoteItemDTO[],
  itemId: string
): QuoteItemDTO | null => {
  for (const item of items) {
    if ('children' in item && item.children?.length) {
      // Check if any direct child matches
      const hasChild = item.children.some(child => getItemId(child) === itemId);

      if (hasChild) {
        return item;
      }

      // Recursively check nested children
      const found = findParentOfItem(item.children, itemId);
      if (found) return found;
    }
  }

  return null;
};

/**
 * Validate child depth - ensure children can't have children
 */
export const validateChildDepth = (
  parentId: string | null,
  quote: QuoteDTO
): boolean => {
  if (!parentId) return true; // Root level is always valid

  const parent = findItemById(quote.items, parentId);

  if (!parent) return false;

  // If parent has a parent, it means we're trying to add a grandchild
  if ('parentID' in parent && parent.parentID) {
    return false; // Not allowed - only one level of nesting
  }

  return true;
};

/**
 * Calculate quote totals recursively
 */
export const calculateQuoteTotals = (
  items: QuoteItemDTO[] | undefined
): QuoteTotals => {
  if (!items || items.length === 0) {
    return {
      totalCost: 0,
      totalSell: 0,
      totalGP: 0,
      totalGPPercentage: 0,
      itemCount: 0,
      unitCount: 0
    };
  }

  let totalCost = 0;
  let totalSell = 0;
  let itemCount = 0;
  let unitCount = 0;

  const processItems = (itemsList: QuoteItemDTO[], isChild = false) => {
    itemsList.forEach(item => {
      if (item.type === 'section') {
        // Sections contain products but don't have their own values
        if ('children' in item && item.children) {
          processItems(item.children);
        }
      } else if (item.type === 'product') {
        const product = item as QuoteProductDTO;
        const quantity = product.quantity || 1;
        const cost = product.cost || 0;
        const sell = product.sell || 0;

        // For children, only include in totals if includeInTotalCost is true
        const shouldIncludeInTotal =
          !isChild || (product as any).includeInTotalCost === true;

        if (shouldIncludeInTotal) {
          totalCost += cost * quantity;
          totalSell += sell * quantity;
        }
        itemCount += 1;
        unitCount += quantity;

        // Process child products
        if (product.children && product.children.length > 0) {
          processItems(product.children, true);
        }
      }
    });
  };

  processItems(items);

  const totalGP = totalSell - totalCost;
  const totalGPPercentage = totalSell > 0 ? (totalGP / totalSell) * 100 : 0;

  return {
    totalCost,
    totalSell,
    totalGP,
    totalGPPercentage,
    itemCount,
    unitCount
  };
};

/**
 * Count total items in quote (including nested)
 */
export const countTotalItems = (items: QuoteItemDTO[]): number => {
  let count = 0;

  const countRecursive = (itemsList: QuoteItemDTO[]) => {
    itemsList.forEach(item => {
      if (item.type === 'product') {
        count += 1;

        if ('children' in item && item.children) {
          countRecursive(item.children);
        }
      } else if (item.type === 'section') {
        // Don't count sections, only products
        if ('children' in item && item.children) {
          countRecursive(item.children);
        }
      }
    });
  };

  countRecursive(items);
  return count;
};

/**
 * Process quote data from API to ensure all products have GP calculated
 * This is needed because the API might not always return GP values
 * Matches the processing logic from old quotes system
 */
export const processQuoteFromAPI = (
  quote: QuoteDTO | undefined
): QuoteDTO | undefined => {
  if (!quote) return quote;

  const processItems = (items: QuoteItemDTO[]): QuoteItemDTO[] =>
    items.map(item => {
      if (item.type === 'product') {
        const product = item as QuoteProductDTO;

        // Calculate GP per unit if we have sell and cost
        if (product.sell != null && product.cost != null) {
          const sell = Number(product.sell);
          const cost = Number(product.cost);

          return {
            ...product,
            totalGrossProfit: sell - cost,
            totalGrossProfitPercentage: calculateGrossProfitPercentage(
              cost,
              sell
            ),
            children: product.children ? processItems(product.children) : []
          } as QuoteItemDTO;
        }

        // Process children even if parent doesn't have pricing
        if (product.children) {
          return {
            ...product,
            children: processItems(product.children)
          } as QuoteItemDTO;
        }

        return product;
      }

      // Process section children
      if (item.type === 'section' && 'children' in item && item.children) {
        return {
          ...item,
          children: processItems(item.children)
        } as QuoteItemDTO;
      }

      return item;
    });

  return {
    ...quote,
    items: processItems(quote.items || [])
  };
};

/**
 * Flatten items for API submission (remove UI-only fields)
 * Based on Swagger: https://revops-api-development.azurewebsites.net/api-docs/#/Quote/EditQuote
 *
 * Returns items in the format expected by the Quote API
 */
export const extractItems = (
  items: QuoteItemDTO[],
  withMetadata = false
): QuoteItemAPIDTO[] =>
  items.map((item): QuoteItemAPIDTO => {
    if (item.type === 'product') {
      const product = item as QuoteProductDTO;

      // Build base product object
      const baseProduct: QuoteItemAPIDTO = {
        ...(product.id && { id: product.id }),
        type: 'product',
        productId: product.productId || product._id,
        quantity: product.quantity || 1,
        sell: product.sell ? Number(product.sell) : 0,
        cost: product.cost ? Number(product.cost) : 0,
        name: product.name || '',
        note: product.note || '',
        sku: product.sku || '',
        minimumQuoteQuantity: product?.minimumQuoteQuantity ?? 1,
        overrideMinimumQuoteQuantity:
          product?.overrideMinimumQuoteQuantity ?? true,
        children: product.children
          ? extractItems(product.children, withMetadata)
          : [],
        isEditable: product.isEditable
      };

      // Add vendor name if available (vendorName may exist on extended product types)
      const productWithVendorName = product as QuoteProductDTO & {
        vendorName?: string;
      };
      if (productWithVendorName.vendorName) {
        baseProduct.vendorName = productWithVendorName.vendorName;
      } else if (product.vendor?.name) {
        baseProduct.vendorName = product.vendor.name;
      }

      if (product?.labelType) {
        baseProduct.labelType = product?.labelType || '';
        baseProduct.customLabel = product?.customLabel || '';
        baseProduct.includeInTotalCost = product?.includeInTotalCost || null;
      }

      // Add freehand-specific fields
      if (product.isFreeHand) {
        baseProduct.isFreeHand = true;
      }

      // Add RFQ item ID if available
      if (product.rfqItemId) {
        baseProduct.rfqItemId = product.rfqItemId;
      }

      // Add metadata if requested
      if (withMetadata) {
        if (product.imageUrl) baseProduct.imageUrl = product.imageUrl;
        // Images might be ProductImage[] or string[] depending on the source
        if (product.images) {
          baseProduct.images = product.images.map((img: unknown) =>
            typeof img === 'string' ? img : (img as { url?: string })?.url || ''
          ) as any;
        }
      }

      return baseProduct;
    }

    if (item.type === 'section') {
      const section = item as QuoteSectionDTO;

      return {
        ...(section.id && { id: section.id }),
        type: 'section',
        name: section.name || '',
        note: section.note || '',
        children: extractItems(section.children || [], withMetadata)
      } as QuoteItemAPIDTO;
    }

    // Fallback for unknown types - return empty section
    return {
      type: 'section',
      name: '',
      note: '',
      children: []
    } as QuoteItemAPIDTO;
  });

/**
 * Build complete quote request body for API submission
 * Based on Swagger: https://revops-api-development.azurewebsites.net/api-docs/#/Quote/EditQuote
 *
 * REQUIRED fields: name, description, opportunity, customerNote, internalNote, items
 * OPTIONAL fields: equipment, validUntil, prospect, user, previewInfo
 */
export const buildQuoteRequestBody = (
  currentQuote: QuoteDTO,
  items: QuoteItemAPIDTO[]
): UpdateQuoteBody => {
  const body: UpdateQuoteBody = {
    name: currentQuote.name,
    description: currentQuote.description || '',
    opportunity: currentQuote.opportunity || '',
    customerNote: currentQuote.customerNote || '',
    internalNote: currentQuote.internalNote || '',
    items
  };

  // Add optional equipment field if present
  const quoteWithEquipment = currentQuote as QuoteDTO & { equipment?: string };
  if (quoteWithEquipment.equipment) {
    body.equipment = quoteWithEquipment.equipment;
  }

  // Add optional validUntil (convert to ISO 8601 format if Date object)
  if (currentQuote.validUntil) {
    body.validUntil =
      currentQuote.validUntil instanceof Date
        ? currentQuote.validUntil.toISOString()
        : currentQuote.validUntil;
  }

  // Add optional prospect for lead/prospect quotes
  if (currentQuote.prospect) {
    body.prospect = currentQuote.prospect;
  }

  // Add optional user information
  if (currentQuote.user) {
    const userWithContact = currentQuote.user as typeof currentQuote.user & {
      contactNumber?: string;
    };
    body.user = {
      firstName: currentQuote.user.firstName,
      lastName: currentQuote.user.lastName,
      email: currentQuote.user.email,
      ...(userWithContact.contactNumber && {
        contactNumber: userWithContact.contactNumber
      })
    };
  }

  // Add optional preview information
  const quoteWithPreview = currentQuote as QuoteDTO & {
    previewInfo?: UpdateQuoteBody['previewInfo'];
  };
  if (quoteWithPreview.previewInfo) {
    body.previewInfo = quoteWithPreview.previewInfo;
  }

  return body;
};

/**
 * Flatten items for API submission (alias for compatibility)
 */
export const flattenItemsForAPI = extractItems;

/**
 * Get total count of product items (excluding child items)
 * Used for displaying "X items" in quote lists
 */
export const getTotalItems = (items: QuoteItemDTO[] = []): number =>
  items.reduce((total: number, item) => {
    if (item.type === 'section' && Array.isArray(item.children)) {
      return (
        total +
        item.children.filter((child: any) => child.type === 'product').length
      );
    }
    if (item.type === 'product' && !('parentID' in item && item.parentID)) {
      return total + 1;
    }
    return total;
  }, 0);

/**
 * Update items with pricing data from API
 * Recursively processes items and their children to apply pricing
 */
export const updateItemsWithPricing = (
  items: QuoteItemDTO[],
  pricingData: PricingData[]
): QuoteItemDTO[] => {
  const updateItem = (item: QuoteItemDTO): QuoteItemDTO => {
    if (item.type === 'product') {
      const product = item as QuoteProductDTO;

      // Only update pricing if it's marked as fetching
      if (!product.isPricingFetching) {
        // Still process children
        if (product.children && product.children.length > 0) {
          return {
            ...product,
            children: updateItemsWithPricing(
              product.children,
              pricingData
            ) as QuoteProductDTO[]
          };
        }
        return product;
      }

      // Find matching pricing data
      const pricingInfo = pricingData.find(p => p.sku === product.sku);

      if (pricingInfo) {
        const { sell } = pricingInfo;
        const { cost } = pricingInfo;
        const totalGrossProfit = sell - cost;
        const totalGrossProfitPercentage = calculateGrossProfitPercentage(
          cost,
          sell
        );

        return {
          ...product,
          isPricingFetching: false,
          isEditable: pricingInfo.isEditable,
          sell,
          cost,
          totalGrossProfit: Number(totalGrossProfit.toFixed(2)),
          totalGrossProfitPercentage: Number(
            totalGrossProfitPercentage.toFixed(2)
          ),
          minGpPercentage:
            pricingInfo.minimumGP != null
              ? (pricingInfo.minimumGP * 100).toFixed(2)
              : '0.00',
          maxGpPercentage:
            pricingInfo.maximumGP != null
              ? (pricingInfo.maximumGP * 100).toFixed(2)
              : '99.99',
          children:
            product.children && product.children.length > 0
              ? (updateItemsWithPricing(
                  product.children,
                  pricingData
                ) as QuoteProductDTO[])
              : []
        };
      }
      // No pricing found, just mark as not fetching
      return {
        ...product,
        isPricingFetching: false,
        children:
          product.children && product.children.length > 0
            ? (updateItemsWithPricing(
                product.children,
                pricingData
              ) as QuoteProductDTO[])
            : []
      };
    }

    // Handle sections
    if (item.type === 'section' && 'children' in item && item.children) {
      return {
        ...item,
        children: updateItemsWithPricing(
          item.children,
          pricingData
        ) as QuoteProductDTO[]
      };
    }

    return item;
  };

  return items.map(updateItem);
};

// Re-export from constants for backwards compatibility
export { getQuoteStatusColor } from '../constants';
